import React from 'react';

interface Matter {
  matter_id: string;
  title?: string;
  description?: string;
  matter_number?: string;
}

interface DeletionError {
  contactId: string;
  contactName: string;
  matters: Matter[];
  error: string;
}

interface ContactDeletionErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errors: DeletionError[];
  title?: string;
}

const ContactDeletionErrorModal: React.FC<ContactDeletionErrorModalProps> = ({
  isOpen,
  onClose,
  errors,
  title = "Cannot Delete Contacts"
}) => {
  if (!isOpen) return null;

  const totalContacts = errors.length;
  const totalMatters = errors.reduce((sum, error) => sum + error.matters.length, 0);

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-in fade-in-0 duration-200"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-2xl w-full max-h-[80vh] overflow-hidden animate-in zoom-in-95 fade-in-0 duration-200"
          role="dialog"
          aria-modal="true"
          aria-labelledby="error-modal-title"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-red-50 dark:bg-red-900/20">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <svg 
                  className="w-5 h-5 text-red-600 dark:text-red-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" 
                  />
                </svg>
              </div>
              <div>
                <h2 
                  id="error-modal-title"
                  className="text-lg font-semibold text-red-900 dark:text-red-100"
                >
                  {title}
                </h2>
                <p className="text-sm text-red-700 dark:text-red-300">
                  {totalContacts} contact{totalContacts !== 1 ? 's' : ''} cannot be deleted due to active matter{totalMatters !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 w-8 h-8 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors duration-200 flex items-center justify-center"
              aria-label="Close dialog"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-96">
            <div className="p-6 space-y-6">
              {/* Explanation */}
              <div className="bg-lex-accent-soft dark:bg-lex-accent/20 rounded-lg p-4 border border-lex-accent dark:border-lex-accent/50">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-lex-accent-soft dark:bg-lex-accent/40 rounded-full flex items-center justify-center mt-0.5">
                    <svg
                      className="w-3 h-3 text-lex-accent dark:text-lex-accent"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-lex-accent dark:text-lex-accent-soft">
                      Why can't these contacts be deleted?
                    </h3>
                    <p className="text-sm text-lex-accent dark:text-lex-accent-soft mt-1">
                      These contacts have active matters associated with them. To maintain data integrity, 
                      you must first close or reassign these matters before deleting the contacts.
                    </p>
                  </div>
                </div>
              </div>

              {/* Error List */}
              <div className="space-y-4">
                {errors.map((error) => (
                  <div 
                    key={error.contactId}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                  >
                    {/* Contact Header */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gradient-to-r from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                            {error.contactName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {error.contactName}
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {error.matters.length} active matter{error.matters.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                          ID: {error.contactId.slice(0, 8)}...
                        </span>
                      </div>
                    </div>

                    {/* Matters List */}
                    <div className="p-4 space-y-3">
                      {error.matters.map((matter) => (
                        <div 
                          key={matter.matter_id}
                          className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-700/50"
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 w-6 h-6 bg-orange-100 dark:bg-orange-900/40 rounded-full flex items-center justify-center mt-1">
                              <svg 
                                className="w-3 h-3 text-orange-600 dark:text-orange-400" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              {/* Matter Name - Prominent */}
                              <h5 className="text-base font-semibold text-orange-900 dark:text-orange-100 mb-1">
                                {matter.title || matter.description || 'Untitled Matter'}
                              </h5>
                              
                              {/* Matter ID - Small text below name */}
                              <p className="text-xs text-orange-600 dark:text-orange-400 font-mono mb-2">
                                Matter ID: {matter.matter_id}
                              </p>
                              
                              {/* Matter Number Badge - if available */}
                              {matter.matter_number && (
                                <div className="mb-2">
                                  <span className="inline-flex items-center text-xs text-orange-700 dark:text-orange-300 font-mono bg-orange-100 dark:bg-orange-900/30 px-2 py-1 rounded">
                                    #{matter.matter_number}
                                  </span>
                                </div>
                              )}
                              
                              {/* Description - if different from title */}
                              {matter.description && matter.title && matter.description !== matter.title && (
                                <p className="text-sm text-orange-700 dark:text-orange-300 mt-2 leading-relaxed">
                                  {matter.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-700/30">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                💡 Tip: Close or reassign matters first, then try deleting the contacts again.
              </div>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-lex-accent focus:ring-offset-2 dark:focus:ring-offset-gray-800"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactDeletionErrorModal; 